/**
 * Notification API Client
 * Provides methods to interact with notification persistence API
 */

import apiClient from './apiClient';
import Logger from '../utils/logger';

/**
 * Create a new notification
 * @param {Object} notification - Notification data
 * @param {string} notification.userId - User ID
 * @param {string} notification.type - Notification type
 * @param {string} notification.title - Notification title
 * @param {string} notification.message - Notification message
 * @param {string} notification.severity - Severity level
 * @param {Object} notification.metadata - Additional metadata
 * @returns {Promise<Object>} Created notification
 */
export async function createNotification(notification) {
  try {
    const response = await apiClient.post('/notifications', notification);
    return response.data.data;
  } catch (error) {
    Logger.error('Failed to create notification', error);
    throw error;
  }
}

/**
 * Get notification history
 * @param {Object} options - Query options
 * @param {number} options.limit - Items per page (default: 50)
 * @param {string} options.continuationToken - Pagination continuation token from previous response
 * @param {string} options.type - Filter by notification type
 * @param {boolean} options.read - Filter by read status
 * @param {string} options.startDate - Filter by start date (ISO string)
 * @param {string} options.endDate - Filter by end date (ISO string)
 * @returns {Promise<Object>} Notification history response with:
 *   - notifications: Array of notification objects
 *   - continuationToken: Token for next page (null if no more)
 *   - hasMore: Boolean indicating if more notifications exist
 *   - limit: Number of items per page
 */
export async function getNotificationHistory(options = {}) {
  try {
    const params = new URLSearchParams();

    if (options.limit) params.append('limit', options.limit);
    if (options.continuationToken) params.append('continuationToken', options.continuationToken);
    if (options.type) params.append('type', options.type);
    if (options.read !== undefined) params.append('read', options.read);
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);

    const response = await apiClient.get(`/notifications?${params.toString()}`);
    return response.data.data;
  } catch (error) {
    Logger.error('Failed to fetch notification history', error);
    throw error;
  }
}

/**
 * Mark a notification as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise<Object>} Updated notification
 */
export async function markNotificationAsRead(notificationId) {
  try {
    const response = await apiClient.put(`/notifications/${notificationId}/read`);
    return response.data.data;
  } catch (error) {
    Logger.error('Failed to mark notification as read', error);
    throw error;
  }
}

/**
 * Mark all notifications as read
 * @returns {Promise<Object>} Response with count of updated notifications.
 *   Includes a `partial` boolean that is `true` when the server returned HTTP 207
 *   (some batches failed after retries). Callers should re-fetch notification state
 *   when `partial` is true.
 */
export async function markAllNotificationsAsRead() {
  try {
    const response = await apiClient.put('/notifications/mark-all-read');
    return { ...response.data.data, partial: response.status === 207 };
  } catch (error) {
    Logger.error('Failed to mark all notifications as read', error);
    throw error;
  }
}

/**
 * Delete a notification
 * @param {string} notificationId - Notification ID
 * @returns {Promise<void>}
 */
export async function deleteNotification(notificationId) {
  try {
    await apiClient.delete(`/notifications/${notificationId}`);
  } catch (error) {
    Logger.error('Failed to delete notification', error);
    throw error;
  }
}

export default {
  createNotification,
  getNotificationHistory,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification
};
